import { Injectable, inject } from '@angular/core';
import { Observable, from, switchMap } from 'rxjs';
import { ApiService } from './api.service';
import { WorkspaceMember, WorkspaceRole, WorkspaceMemberStatus, User } from '@crowdctrl/core';

/**
 * Service for managing workspace members.
 * Handles CRUD operations for workspace memberships.
 */
@Injectable({
  providedIn: 'root',
})
export class WorkspaceMemberService {
  private apiService = inject(ApiService);
  private baseUrl: string = '';

  setBaseUrl(url: string): void {
    this.baseUrl = url;
  }

  /**
   * Get all members of a workspace with pagination
   */
  getWorkspaceMembers(
    workspaceId: string,
    limit?: number,
    cursor?: string,
  ): Observable<{
    members: WorkspaceMember[];
    pagination: {
      limit: number;
      hasNext: boolean;
      nextCursor: string | null;
      totalCount: number;
      retrievedAt: string;
    };
  }> {
    const params: Record<string, string | number> = {
      workspaceId,
    };
    if (limit) {
      params['limit'] = limit;
    }
    if (cursor) {
      params['cursor'] = cursor;
    }
    return from(
      this.apiService.get<{
        members: WorkspaceMember[];
        pagination: {
          limit: number;
          hasNext: boolean;
          nextCursor: string | null;
          totalCount: number;
          retrievedAt: string;
        };
      }>(this.baseUrl, `/workspace_members_api/api/workspaces/${workspaceId}/members`, params),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Get all members of a workspace with isInSession flag for a specific session
   */
  getWorkspaceMembersForSession(
    workspaceId: string,
    sessionId: string,
    limit?: number,
    cursor?: string,
  ): Observable<{
    members: WorkspaceMember[];
    pagination: {
      limit: number;
      hasNext: boolean;
      nextCursor: string | null;
      totalCount: number;
      retrievedAt: string;
    };
  }> {
    const params: Record<string, string | number> = {
      workspaceId,
      sessionId,
    };
    if (limit) {
      params['limit'] = limit;
    }
    if (cursor) {
      params['cursor'] = cursor;
    }
    return from(
      this.apiService.get<{
        members: WorkspaceMember[];
        pagination: {
          limit: number;
          hasNext: boolean;
          nextCursor: string | null;
          totalCount: number;
          retrievedAt: string;
        };
      }>(this.baseUrl, `/workspace_members_api/api/workspaces/${workspaceId}/members`, params),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Get a specific workspace member by member ID
   */
  getWorkspaceMember(
    workspaceId: string,
    memberId: string,
  ): Observable<{ member: WorkspaceMember }> {
    return from(
      this.apiService.get<{ member: WorkspaceMember }>(
        this.baseUrl,
        `/workspace_members_api/api/workspaces/${workspaceId}/members/${memberId}`,
        { workspaceId },
      ),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Get user's role in a workspace
   */
  getUserWorkspaceRole(workspaceId: string, userId: string): Observable<WorkspaceRole | null> {
    return from(
      this.apiService.get<{ role: WorkspaceRole | null }>(
        this.baseUrl,
        `/workspace_members_api/api/workspaces/${workspaceId}/members/${userId}/role`,
        { workspaceId },
      ),
    ).pipe(
      switchMap((obs) => obs),
      switchMap((response) => {
        return new Observable<WorkspaceRole | null>((subscriber) => {
          subscriber.next(response.role);
          subscriber.complete();
        });
      }),
    );
  }

  /**
   * Get available tenant users for workspace membership (Coordinator or TenantAdmin only)
   * @param includeExisting If true, returns all tenant users including those already in the workspace
   */
  getAvailableWorkspaceUsers(
    workspaceId: string,
    limit?: number,
    cursor?: string,
    includeExisting: boolean = true,
  ): Observable<{
    users: User[];
    pagination: {
      limit: number;
      hasNext: boolean;
      nextCursor: string | null;
      totalCount: number | null;
      retrievedAt: string;
    };
  }> {
    const params: {
      workspaceId: string;
      limit?: number;
      cursor?: string;
      includeExisting?: string;
    } = {
      workspaceId,
    };
    if (limit !== undefined) {
      params.limit = limit;
    }
    if (cursor) {
      params.cursor = cursor;
    }
    if (includeExisting) {
      params.includeExisting = 'true';
    }
    return from(
      this.apiService.get<{
        users: User[];
        pagination: {
          limit: number;
          hasNext: boolean;
          nextCursor: string | null;
          totalCount: number | null;
          retrievedAt: string;
        };
      }>(
        this.baseUrl,
        `/workspace_members_api/api/workspaces/${workspaceId}/available-users`,
        params,
      ),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Create a workspace member by userId (must be an active tenant user)
   */
  createWorkspaceMember(
    workspaceId: string,
    userId: string,
    role: WorkspaceRole,
    startDate?: Date,
    endDate?: Date,
  ): Observable<{ member: WorkspaceMember }> {
    const body: {
      userId: string;
      role: WorkspaceRole;
      startDate?: string;
      endDate?: string;
    } = {
      userId,
      role,
    };

    // Add optional dates as ISO strings
    if (startDate) {
      body.startDate = startDate.toISOString();
    }
    if (endDate) {
      body.endDate = endDate.toISOString();
    }

    return from(
      this.apiService.post<{ member: WorkspaceMember }, typeof body>(
        this.baseUrl,
        `/workspace_members_api/api/workspaces/${workspaceId}/members?workspaceId=${encodeURIComponent(workspaceId)}`,
        body,
      ),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Add a member to a workspace with a specific role (legacy - use createWorkspaceMember)
   * @deprecated Use createWorkspaceMember instead
   */
  addWorkspaceMember(
    workspaceId: string,
    userId: string,
    role: WorkspaceRole,
  ): Observable<{ member: WorkspaceMember }> {
    return from(
      this.apiService.post<{ member: WorkspaceMember }, { userId: string; role: WorkspaceRole }>(
        this.baseUrl,
        `/workspace_members_api/api/workspaces/${workspaceId}/members?workspaceId=${encodeURIComponent(workspaceId)}`,
        {
          userId,
          role,
        },
      ),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Update a workspace member's role by member ID
   */
  updateWorkspaceMemberRole(
    workspaceId: string,
    memberId: string,
    role: WorkspaceRole,
  ): Observable<{ member: WorkspaceMember }> {
    return from(
      this.apiService.put<{ member: WorkspaceMember }, { role: WorkspaceRole }>(
        this.baseUrl,
        `/workspace_members_api/api/workspaces/${workspaceId}/members/${memberId}?workspaceId=${encodeURIComponent(workspaceId)}`,
        {
          role,
        },
      ),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Update a workspace member's status by member ID
   */
  updateWorkspaceMemberStatus(
    workspaceId: string,
    memberId: string,
    status: WorkspaceMemberStatus,
  ): Observable<{ member: WorkspaceMember }> {
    return from(
      this.apiService.put<{ member: WorkspaceMember }, { status: WorkspaceMemberStatus }>(
        this.baseUrl,
        `/workspace_members_api/api/workspaces/${workspaceId}/members/${memberId}?workspaceId=${encodeURIComponent(workspaceId)}`,
        {
          status,
        },
      ),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Update optional membership window (clears a bound when value is null).
   */
  updateWorkspaceMemberMembershipDates(
    workspaceId: string,
    memberId: string,
    startDate: Date | null | undefined,
    endDate: Date | null | undefined,
  ): Observable<{ member: WorkspaceMember }> {
    const body: { startDate: string | null; endDate: string | null } = {
      startDate: startDate ? startDate.toISOString() : null,
      endDate: endDate ? endDate.toISOString() : null,
    };
    return from(
      this.apiService.put<{ member: WorkspaceMember }, typeof body>(
        this.baseUrl,
        `/workspace_members_api/api/workspaces/${workspaceId}/members/${memberId}?workspaceId=${encodeURIComponent(workspaceId)}`,
        body,
      ),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Remove a member from a workspace by member ID
   */
  removeWorkspaceMember(workspaceId: string, memberId: string): Observable<void> {
    return from(
      this.apiService.delete<void>(
        this.baseUrl,
        `/workspace_members_api/api/workspaces/${workspaceId}/members/${memberId}?workspaceId=${encodeURIComponent(workspaceId)}`,
      ),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * List workspaces the current user is a member of (across all workspaces)
   */
  listMyWorkspaces(
    limit?: number,
    cursor?: string,
  ): Observable<{
    items: Array<{
      workspaceId: string;
      workspaceName: string;
      role?: string;
      status?: string;
      createdAt?: string;
      createdBy?: { id?: string; email?: string };
    }>;
    retrievedAt?: string;
    pagination?: {
      limit: number;
      hasNext: boolean;
      nextCursor: string | null;
      totalCount?: number;
      retrievedAt: string;
    };
  }> {
    const params: Record<string, string> = {};
    if (limit) {
      params['limit'] = limit.toString();
    }
    if (cursor) {
      params['cursor'] = cursor;
    }
    return from(
      this.apiService.get<{
        items: Array<{
          workspaceId: string;
          workspaceName: string;
          role?: string;
          status?: string;
          createdAt?: string;
          createdBy?: { id?: string; email?: string };
        }>;
        retrievedAt?: string;
        pagination?: {
          limit: number;
          hasNext: boolean;
          nextCursor: string | null;
          totalCount?: number;
          retrievedAt: string;
        };
      }>(this.baseUrl, `/workspace_members_api/api/workspaces/memberships`, params),
    ).pipe(switchMap((obs) => obs));
  }
}
